import { useEffect, useRef } from 'react';

export const VEHICLE_CREATED_EVENT = 'vehicle:created';
export const VEHICLE_DRIVER_ASSIGNED_EVENT = 'vehicle:driver-assigned';

// ⚠️ VALIDACIÓN: Rastrear eventos procesados para evitar duplicados
const processedEvents = new Map<string, number>();
const EVENT_EXPIRY_MS = 5000; // Los eventos expiran después de 5s

const generateEventId = (type: string, detail: any): string => {
  if (type === VEHICLE_CREATED_EVENT) {
    return `created:${detail.id}:${detail.plate}`;
  }
  return `assigned:${detail.vehicleId}:${detail.driverId}`;
};

const isEventProcessed = (eventId: string): boolean => {
  const timestamp = processedEvents.get(eventId);
  if (!timestamp) return false;
  
  const now = Date.now();
  if (now - timestamp > EVENT_EXPIRY_MS) {
    processedEvents.delete(eventId);
    return false;
  }
  
  return true;
};

const markEventProcessed = (eventId: string) => {
  processedEvents.set(eventId, Date.now());
};

export const dispatchVehicleCreated = (vehicle: any) => {
  const eventId = generateEventId(VEHICLE_CREATED_EVENT, vehicle);
  
  // ⚠️ VALIDACIÓN: No disparar si ya fue procesado recientemente
  if (isEventProcessed(eventId)) {
    console.warn(`[VehicleEvents] Event already processed: ${eventId}`);
    return;
  }
  
  markEventProcessed(eventId);
  window.dispatchEvent(new CustomEvent(VEHICLE_CREATED_EVENT, { 
    detail: { ...vehicle, eventId } 
  }));
};

export const dispatchDriverAssigned = (vehicleId: string, driverId: string) => {
  const eventId = generateEventId(VEHICLE_DRIVER_ASSIGNED_EVENT, { vehicleId, driverId });
  
  // ⚠️ VALIDACIÓN: No disparar si ya fue procesado recientemente
  if (isEventProcessed(eventId)) {
    console.warn(`[VehicleEvents] Event already processed: ${eventId}`);
    return;
  }
  
  markEventProcessed(eventId);
  window.dispatchEvent(new CustomEvent(VEHICLE_DRIVER_ASSIGNED_EVENT, { 
    detail: { vehicleId, driverId, eventId } 
  }));
};

export const useVehicleEvents = (
  onVehicleCreated?: (vehicle: any) => void,
  onDriverAssigned?: (data: { vehicleId: string; driverId: string }) => void
) => {
  // ⚠️ VALIDACIÓN: Usar ref para evitar procesamiento múltiple
  const processedEventIds = useRef(new Set<string>());
  
  useEffect(() => {
    const handleCreated = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      
      // ⚠️ VALIDACIÓN: Verificar si ya procesamos este evento
      if (processedEventIds.current.has(detail.eventId)) {
        console.warn(`[useVehicleEvents] Duplicate event ignored: ${detail.eventId}`);
        return;
      }
      
      processedEventIds.current.add(detail.eventId);
      onVehicleCreated?.(detail);
      
      // Limpiar después de 5s
      setTimeout(() => {
        processedEventIds.current.delete(detail.eventId);
      }, EVENT_EXPIRY_MS);
    };
    
    const handleAssigned = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      
      // ⚠️ VALIDACIÓN: Verificar si ya procesamos este evento
      if (processedEventIds.current.has(detail.eventId)) {
        console.warn(`[useVehicleEvents] Duplicate event ignored: ${detail.eventId}`);
        return;
      }
      
      processedEventIds.current.add(detail.eventId);
      onDriverAssigned?.(detail);
      
      // Limpiar después de 5s
      setTimeout(() => {
        processedEventIds.current.delete(detail.eventId);
      }, EVENT_EXPIRY_MS);
    };

    if (onVehicleCreated) {
      window.addEventListener(VEHICLE_CREATED_EVENT, handleCreated);
    }
    if (onDriverAssigned) {
      window.addEventListener(VEHICLE_DRIVER_ASSIGNED_EVENT, handleAssigned);
    }

    return () => {
      window.removeEventListener(VEHICLE_CREATED_EVENT, handleCreated);
      window.removeEventListener(VEHICLE_DRIVER_ASSIGNED_EVENT, handleAssigned);
    };
  }, [onVehicleCreated, onDriverAssigned]);
};


